// PasswordResetConfirm.js - Updated with pre-filled email and resend code link and timer
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PasswordInput from '../components/PasswordInput';
import { AuthService } from '../services/auth';
import EmailInput from '../components/EmailInput';

// It's a good practice to define constants for values like the cooldown period
const RESEND_COOLDOWN_SECONDS = 60;

// We assume the AuthService has a method for the initial password reset request,
// which we can reuse to resend the code.
const authService = new AuthService();

const PasswordResetConfirm = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // --- New state for the resend timer ---
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [canResend, setCanResend] = useState(false);
  
  const navigate = useNavigate();

  // Pre-fill email from sessionStorage if available
  useEffect(() => {
    const savedEmail = sessionStorage.getItem('resetEmail');
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, []);

  // --- useEffect for the countdown timer ---
  useEffect(() => {
    // If the cooldown is active, create a timer
    if (resendCooldown > 0) {
      const timerId = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      
      // Clear the timer if the component unmounts or the cooldown changes
      return () => clearTimeout(timerId);
    } else {
      // When the cooldown finishes, enable the resend button
      setCanResend(true);
    }
  }, [resendCooldown]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null); // Clear previous messages
    
    try {
      await authService.confirmPassword(email, code, newPassword);
      setMessage('Password reset successful. You can now log in.');
      sessionStorage.removeItem('resetEmail');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.');
      setLoading(false);
    }
  };

  // --- Handler for resending the verification code ---
  const handleResendCode = async () => {
    if (!canResend) return; // Prevent clicking when on cooldown

    setLoading(true); // Optional: show a loading state on the main button
    setError(null);
    setMessage(null);
    setCanResend(false); // Disable the button immediately

    try {
      // We assume the auth service has a method like `forgotPassword` 
      // or a dedicated `resendCode` method that takes the user's email.
      await authService.forgotPassword(email); 
      setMessage('A new verification code has been sent.');
      // Restart the cooldown timer
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(err.message || 'Failed to resend code. Please try again.');
      setCanResend(true); // Re-enable the button if the request fails
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-5 text-center text-gray-800">Set New Password</h1>
      {error && <p className="text-red-500 mb-3 text-sm text-center">{error}</p>}
      {message && <p className="text-green-500 mb-3 text-sm text-center">{message}</p>}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <EmailInput 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            // Disable if the email was pre-filled to ensure the correct user
            disabled={!!sessionStorage.getItem('resetEmail')}
          />
        </div>
        <div className="mb-4">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            placeholder="Verification Code"
            required
          />
        </div>
        <div className="mb-6">
          <PasswordInput value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-black text-white py-3 rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transition-opacity disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>
      </form>
      
      <div className="text-center text-sm text-gray-600 mt-4">
        <p>
          Didn't receive the code?{' '}
          {/* --- Updated Resend Code Button --- */}
          <button
            type="button"
            onClick={handleResendCode}
            disabled={!canResend || loading}
            className={`font-medium ${
              canResend
                ? 'text-blue-600 hover:underline focus:outline-none'
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            {canResend ? 'Resend Code' : `Resend in ${resendCooldown}s`}
          </button>
        </p>
        <p className="mt-2">
          Remember your password?{' '}
          <Link to="/login" className="font-medium text-blue-600 hover:underline">Back to Login</Link>
        </p>
      </div>
    </div>
  );
};

export default PasswordResetConfirm;